import { useRef, useState } from 'react';
import { createFilter } from 'react-select';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { ModalButtonWithForm } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';
import { FormGroup, Select, TextArea } from 'components/common/form';
import {
  CompetenciesCard,
  CompetenciesCardLevel
} from 'components/competencies';

import type { FormApi } from 'informed';
import type { CompetencyInProduct } from 'shared-types';
import type { GetCompetenciesRes } from 'shared-types/responses';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';

type CompetencyOption = {
  allowUserSelfAdding: boolean;
  categories: string[];
  description: string;
  id: number;
  levelDescription: string | null;
  levelId: number | null;
  levelName: string | null;
  name: string;
};

interface Props {
  allowOnlyUserEditableCompetencies?: boolean;
  buttonCategory?: ButtonCategory;
  buttonIcon?: IconProp;
  buttonSize?: ButtonSize;
  buttonText?: string;
  excludedCompetencies?: CompetencyInProduct[];
  grantOnly?: boolean;
  initialCompetencies?: CompetencyInProduct[];
  isGranting?: boolean;
  modalActionText?: string;
  modalTitle?: string;
  onSubmit: (
    competencies: {
      id: number;
      levelId: number | null;
      comment?: string | null;
    }[]
  ) => Promise<void>;
  selfAddOnly?: boolean;
}

const CompetenciesEditor = ({
  allowOnlyUserEditableCompetencies = false,
  buttonCategory,
  buttonIcon,
  buttonSize,
  buttonText,
  excludedCompetencies = [],
  grantOnly = false,
  initialCompetencies = [],
  isGranting = false,
  modalActionText,
  modalTitle,
  onSubmit,
  selfAddOnly = false
}: Props) => {
  const formApi = useRef<FormApi>(undefined);

  const { language, messages, theme } = useReduxState();

  const [category, setCategory] = useState('all');

  const { data } = useApiData<GetCompetenciesRes>({
    url:
      `/admin/competencies?limit=-1` +
      `${grantOnly ? '&grantOnly=true' : ''}` +
      `${selfAddOnly ? '&selfAddOnly=true' : ''}`
  });

  const categories = data?.categories || [];

  let competencyOptions: CompetencyOption[] = [];

  data?.competencyObjs.forEach((competency) => {
    if (competency.levels?.length) {
      competency.levels.forEach((level) => {
        competencyOptions.push({
          allowUserSelfAdding: competency.allowUserSelfAdding,
          categories: competency.categories,
          description:
            competency.descriptionLocales?.[language] || competency.description,
          id: competency.id,
          levelDescription:
            level.descriptionLocales?.[language] || level.description,
          levelId: level.id,
          levelName: level.nameLocales?.[language] || level.name,
          name: competency.nameLocales?.[language] || competency.name
        });
      });
    } else {
      competencyOptions.push({
        allowUserSelfAdding: competency.allowUserSelfAdding,
        categories: competency.categories,
        description:
          competency.descriptionLocales?.[language] || competency.description,
        id: competency.id,
        levelDescription: null,
        levelId: null,
        levelName: null,
        name: competency.nameLocales?.[language] || competency.name
      });
    }
  });

  // filter out excluded competencies
  if (excludedCompetencies.length) {
    competencyOptions = competencyOptions.filter(
      (competencyOption) =>
        !excludedCompetencies.some(
          (exlcudedCompetency) =>
            exlcudedCompetency.id === competencyOption.id &&
            (exlcudedCompetency.level?.id || null) === competencyOption.levelId
        )
    );
  }

  if (allowOnlyUserEditableCompetencies && !selfAddOnly) {
    competencyOptions = competencyOptions.filter(
      (competencyOption) => competencyOption.allowUserSelfAdding
    );
  }

  const handleRemove = (competencyId: number, levelId?: number) => {
    const competencieValues = formApi.current.getState().values
      .competencies as string[];

    const updatedCompetencyValues = competencieValues.filter(
      (competencyValue) => competencyValue !== `${competencyId}-${levelId || 0}`
    );

    formApi.current.setValue('competencies', updatedCompetencyValues);
  };

  const handleSubmit = async (values) => {
    const competencies = values.competencies?.map((competencyValue) => {
      const valueParts = competencyValue.split('-');
      return {
        id: Number(valueParts[0]),
        levelId: Number(valueParts[1]) || null,
        comment: (values[`comment-${competencyValue}`] as string) || null
      };
    });

    await onSubmit(competencies);
  };

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: 'any' as const,
    stringify: (option) => {
      const competency = competencyOptions.find(
        (competencyOption) =>
          `${competencyOption.id}-${competencyOption.levelId || 0}` ===
          option.value
      );
      return `${competency?.name} ${competency?.levelName || ''}`;
    },
    trim: true
  };

  const initialValues = {
    competencies: initialCompetencies.map(
      (competency) => `${competency.id}-${competency.level?.id || 0}`
    )
  };

  if (
    allowOnlyUserEditableCompetencies &&
    !selfAddOnly &&
    !data?.competencyObjs?.some((competency) => competency.allowUserSelfAdding)
  ) {
    return null;
  }

  // no button rather than an empty picker
  if ((grantOnly || selfAddOnly) && !competencyOptions.length) return null;

  return (
    <ModalButtonWithForm
      buttonCategory={buttonCategory || 'light'}
      buttonIcon={buttonIcon || 'pen'}
      buttonSize={buttonSize || 'small'}
      buttonText={buttonText || messages.profileEditProfileCompetences}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={modalActionText || messages.commonSaveChanges}
      modalTitle={modalTitle || messages.profileEditProfileCompetences}
      modalWidth="medium"
      onSubmit={handleSubmit}
      contentStyle={{ padding: 0 }}
      pristineIsValid={false}
      preventSubmit={true}
      setFormApi={(form) => (formApi.current = form)}
      stopPropagation={true}
    >
      {(formState) => (
        <div>
          <div className="header">
            <div className="select">
              <FormGroup label={messages.analyticsCompetencies} maxWidth="100%">
                <Select
                  field="competencies"
                  filterOption={createFilter(filterConfig)}
                  id="profile-competencies-add-competencies"
                  isMulti={true}
                  isSearchable={true}
                  options={competencyOptions
                    .filter(
                      (competency) =>
                        category === 'all' ||
                        competency.categories?.includes(category)
                    )
                    .filter(
                      (competency) =>
                        !formState.values?.competencies?.some(
                          (value) =>
                            value.id === competency.id &&
                            value.levelId === competency.levelId
                        )
                    )
                    .map((competency) => ({
                      label: (
                        <span>
                          {competency.name}
                          {competency.levelName ? (
                            <span
                              style={{ fontWeight: 400 }}
                            >{`: ${competency.levelName}`}</span>
                          ) : (
                            ''
                          )}
                        </span>
                      ),
                      value: `${competency.id}-${competency.levelId || 0}`
                    }))}
                  showLabelForSelectedElement={true}
                  value={formState?.values?.competencies
                    ?.map((competencyValue) => {
                      const competency = competencyOptions.find(
                        (competencyOption) =>
                          `${competencyOption.id}-${
                            competencyOption.levelId || 0
                          }` === competencyValue
                      );
                      if (!competency) return null;

                      return {
                        label: (
                          <span>
                            {competency.name}
                            {competency.levelName ? (
                              <span
                                style={{ fontWeight: 400 }}
                              >{`: ${competency.levelName}`}</span>
                            ) : (
                              ''
                            )}
                          </span>
                        ),
                        value: `${competency.id}-${competency.levelId || 0}`
                      };
                    })
                    .filter(Boolean)}
                />
              </FormGroup>
            </div>

            <div className="category">
              <DropdownSelectable
                category="lighter"
                defaultValue="all"
                titleMaxWidth={125}
                onChange={(category: string) => setCategory(category)}
                options={[
                  { text: messages.commonAll, value: 'all' },
                  ...(categories.map((category) => ({
                    text: category,
                    value: category
                  })) || [])
                ]}
                renderAsFixed={true}
                selectedValueBefore={`${messages.commonCategory}:`}
                showSelectedText={true}
                size="medium"
                value={category}
              />
            </div>
          </div>

          {formState?.values?.competencies?.length ? (
            <div className="competencies">
              {formState.values.competencies.map((competencyValue: string) => {
                const competency = competencyOptions.find(
                  (competencyOption) =>
                    `${competencyOption.id}-${
                      competencyOption.levelId || 0
                    }` === competencyValue
                );
                if (!competency) return null;

                const competencyKey = `${competency.id}-${
                  competency.levelId || 0
                }`;

                return (
                  <div
                    className="competency"
                    key={`${competency.id}-${competency.levelId}`}
                  >
                    <CompetenciesCard
                      description={competency.description}
                      id={competency.id}
                      isChecked={!competency.levelId}
                      isCheckedPending={!competency.levelId && !isGranting}
                      name={competency.name}
                      onRemove={competency.levelId ? undefined : handleRemove}
                    >
                      {competency.levelId ? (
                        <CompetenciesCardLevel
                          competencyId={competency.id}
                          description={competency.levelDescription}
                          id={competency.levelId}
                          isChecked={true}
                          isCheckedPending={!isGranting}
                          name={competency.levelName}
                          onRemove={handleRemove}
                        />
                      ) : null}

                      {isGranting ? (
                        <div className="competency-comment">
                          <FormGroup
                            label={messages.commonComment}
                            labelIsOptional={true}
                            maxWidth="100%"
                          >
                            <TextArea
                              field={`comment-${competencyKey}`}
                              id={`profile-competencies-add-comment-${competencyKey}`}
                              maxLength={250}
                              preventNewLine={true}
                              rows={3}
                            />
                          </FormGroup>
                        </div>
                      ) : null}
                    </CompetenciesCard>
                  </div>
                );
              })}
            </div>
          ) : null}

          <style jsx>{`
            .header {
              align-items: flex-end;
              display: flex;
              gap: 15px;
              padding: 15px 15px 20px;
            }
            .select {
              flex-grow: 1;
            }
            .competencies {
              border-top: 1px solid ${theme.lightGreyDark};
              background-color: ${theme.lightGreyLighter};
              display: flex;
              flex-direction: column;
              gap: 15px;
              padding: 15px;
            }
            .competency-comment {
              padding: 0 15px 15px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default CompetenciesEditor;
